import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Leaf, ChevronRight, Camera } from "lucide-react";
import { TopBar, BottomNav } from "../components/Nav";
import { API } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";

export default function HistoryPage() {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();
  const { t } = useLang();

  useEffect(() => {
    axios.get(`${API}/scans`).then((r) => setScans(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-24 md:pb-10">
      <TopBar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <h1 className="cd-heading text-3xl font-extrabold">{t("history.title")}</h1>
        <p className="text-[#52796F] text-sm mb-5">{t("history.sub")}</p>

        {loading && (
          <div className="flex justify-center py-20" data-testid="history-loading">
            <Loader2 className="w-6 h-6 animate-spin text-[#52796F]" />
          </div>
        )}

        {!loading && scans.length === 0 && (
          <div className="bg-white border border-[#E5E0D8] rounded-2xl p-10 text-center" data-testid="history-empty">
            <div className="w-14 h-14 bg-[#F4F1EA] rounded-2xl mx-auto flex items-center justify-center">
              <Leaf className="w-7 h-7 text-[#52796F]" />
            </div>
            <h3 className="cd-heading font-bold text-lg mt-4">{t("history.empty")}</h3>
            <p className="text-sm text-[#52796F] mt-1">{t("history.emptySub")}</p>
            <button
              data-testid="go-to-dashboard-btn"
              onClick={() => nav("/dashboard")}
              className="mt-5 bg-[#1B4332] text-white hover:bg-[#2D6A4F] px-6 py-3 rounded-full font-bold inline-flex items-center gap-2"
            >
              <Camera className="w-4 h-4" /> {t("history.scanNow")}
            </button>
          </div>
        )}

        {!loading && scans.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="history-list">
            {scans.map((s) => (
              <div
                key={s.id}
                data-testid={`history-item-${s.id}`}
                onClick={() => nav(`/scan/${s.id}`)}
                className="bg-white rounded-2xl border border-[#E5E0D8] overflow-hidden cursor-pointer cd-lift"
              >
                <img
                  src={s.thumbnail || s.image_base64}
                  alt=""
                  className="w-full h-40 object-cover bg-[#F4F1EA]"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-xs text-[#52796F]">{s.result?.crop_name || "Plant"}</div>
                      <div className="font-bold text-[#1C1C1C] mt-0.5">
                        {s.result?.healthy ? t("diag.healthy") : s.result?.disease_name || t("diag.unknown")}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#52796F] mt-1" />
                  </div>
                  <div className="flex items-center justify-between mt-3 text-xs text-[#52796F]">
                    <span>{t("history.confident", { c: s.result?.confidence || 0 })}</span>
                    <span>{new Date(s.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <BottomNav />
    </div>
  );
}
